import { Locale } from "@tableyeti/merchant-service";
import { nextTick } from "vue";
import { createI18n, I18n, useI18n } from "vue-i18n";
import { en, es, fr, it } from "vuetify/locale";
import { createVueI18nAdapter } from "vuetify/locale/adapters/vue-i18n";
import enYeti from "@/assets/i18n/en.json";

const messages = {
  en: { $vuetify: { ...en }, ...enYeti },
};

type MessageSchema = typeof messages.en;

const datetimeFormats = {
  en: {
    short: {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
    full: {
      weekday: "short",
      day: "numeric",
      month: "long",
      year: "numeric",
    },
  },
  es: {
    short: {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
    full: {
      weekday: "short",
      day: "numeric",
      month: "long",
      year: "numeric",
    },
  },
  fr: {
    short: {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
    full: {
      weekday: "short",
      day: "numeric",
      month: "long",
      year: "numeric",
    },
  },
  it: {
    short: {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
    full: {
      weekday: "short",
      day: "numeric",
      month: "long",
      year: "numeric",
    },
  },
} as const;

const numberFormats = {
  en: {
    currency: {
      style: "currency",
      currency: "GBP",
    },
  },
  es: {
    currency: {
      style: "currency",
      currency: "EUR",
    },
  },
  fr: {
    currency: {
      style: "currency",
      currency: "EUR",
    },
  },
  it: {
    currency: {
      style: "currency",
      currency: "EUR",
    },
  },
} as const;

export const i18n = createI18n<[MessageSchema], "en">({
  legacy: false, // Vuetify does not support the legacy mode of vue-i18n
  globalInjection: false, // we favor the composition API
  locale: ((locale) => (Object.values(Locale).includes(locale as Locale) ? locale : "en-GB"))(
    localStorage.getItem("locale") || window.navigator.language,
  ),
  fallbackLocale: "en-GB",
  messages,
  datetimeFormats,
  numberFormats,
});

export const i18nAdapter = createVueI18nAdapter({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-empty-object-type
  i18n: i18n as unknown as I18n<any, {}, {}, string, false>,
  useI18n,
}) as unknown;

const getResourceMessages = (r: { default: MessageSchema }) => r.default || r;

const vuetifyLocales = {
  en: en,
  es: es,
  fr: fr,
  it: it,
};

export async function loadLocaleMessages(locale: string) {
  if (!Object.values(Locale).includes(locale as Locale)) {
    return nextTick();
  }

  const messages = await import(`@/assets/i18n/${locale}.json`)
    .catch(() => import(`@/assets/i18n/${locale.split("-")[0]}.json`))
    .then(getResourceMessages);

  i18n.global.setLocaleMessage(locale, {
    ...messages,
    $vuetify: vuetifyLocales[locale as keyof typeof vuetifyLocales],
  });

  return nextTick();
}
